import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ElementOptions,
  ElementsOptions,
  StripeCardComponent,
  StripeService,
} from 'ngx-stripe';
import { FormControl, FormGroup } from '@angular/forms';
import { BillingService } from '../../../lib/services/billingService';
import { take } from 'rxjs/operators';
import { BillingInfoForm } from '../billing-info/billing-info.form';
import { PaymentStatusRequested } from '../../../core/actions/paymentStatus';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import { selectPaymentState } from '../../../core/selectors/paymentState';

enum PaymentStates {
  preview = 0,
  editing,
  loading,
}

@Component({
  selector: 'mj-billing-unpaid',
  templateUrl: './billing-unpaid.component.html',
  styleUrls: ['./billing-unpaid.component.scss'],
})
export class BillingUnpaidComponent implements OnInit {
  @Input() plan;
  loading: boolean;
  paymentState = PaymentStates.editing;

  elementsOptions: ElementsOptions;

  unit_id;
  cardInfo;

  billingInfo;

  billingForm: FormGroup = BillingInfoForm;

  @ViewChild(StripeCardComponent, { static: false })
  card: StripeCardComponent;

  @Output() paymentFinished = new EventEmitter();

  cardForm = new FormGroup({
    name: new FormControl(''),
  });

  cardOptions: ElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        lineHeight: '40px',
        fontWeight: 300,
        fontFamily: '"Avenir", sans-serif',
        fontSize: '18px',
        '::placeholder': {
          color: '#CFD7E0',
        },
      },
    },
  };

  constructor(
    private stripeService: StripeService,
    private billingService: BillingService,
    private store: Store<fromRoot.State>
  ) {}

  ngOnInit() {
    this.elementsOptions = {
      locale: 'auto',
    };

    this.unit_id = localStorage.getItem('unit_id');
    this.billingService
      .fetchBillingInfo(this.unit_id)
      .pipe(take(1))
      .subscribe(billingInfo => {
        this.cardInfo = billingInfo.sources.data[0];
        this.billingInfo = billingInfo;
        this.assignFormValues();
        if (this.cardInfo) {
          this.paymentState = PaymentStates.preview;
        } else {
          this.paymentState = PaymentStates.editing;
        }
      });
  }

  submitCard() {
    const name = this.cardForm.get('name').value;
    return new Promise((res, rej) => {
      this.stripeService
        .createToken(this.card.getCard(), { name })
        .pipe(take(1))
        .subscribe(
          data => {
            res(data.token ? data.token.id : null);
          },
          e => {
            rej(e);
          }
        );
    });
  }

  submitBillingInfo(token?) {
    return new Promise((res, rej) => {
      if (!this.billingForm.valid) {
        rej('Your email address is invalid');
      }

      const request = this.billingForm.value;

      if (token) {
        request['source'] = token;
      }

      this.billingService
        .updateBillingInfo(this.unit_id, request)
        .pipe(take(1))
        .subscribe(
          data => {
            res(data);
          },
          e => {
            rej(e);
          }
        );
    });
  }

  submitSelectedPlan() {
    return new Promise((res, rej) => {
      const request = {
        plan_id: this.plan.id,
        quantity: 1,
      };

      this.billingService
        .activateSubscription(this.unit_id, request)
        .pipe(take(1))
        .subscribe(
          sub => {
            res(sub);
          },
          e => {
            rej(e);
          }
        );
    });
  }

  assignFormValues() {
    this.billingForm.patchValue({
      name: this.billingInfo.shipping.name,
      email: this.billingInfo.email,
      line1: this.billingInfo.shipping.address.line1,
      country: this.billingInfo.shipping.address.country,
      description: this.billingInfo.description,
    });
  }

  async onFinish() {
    if (!this.billingForm.valid) {
      return;
    }

    this.loading = true;

    this.submitCard()
      .then(token => this.submitBillingInfo(token))
      .then(() => this.submitSelectedPlan())
      .then(() =>
        this.store.dispatch(new PaymentStatusRequested(this.unit_id))
      );

    this.store
      .select(selectPaymentState)
      .pipe(take(3))
      .subscribe(payment => {
        if (
          payment.status === 'pending' ||
          payment.status === 'active' ||
          payment.status === 'trailing'
        ) {
          this.paymentFinished.emit();
          this.loading = false;
        }
      });
  }

  openEditCard() {
    this.paymentState = PaymentStates.editing;
  }

  cancelEditCard() {
    this.paymentState = PaymentStates.preview;
  }
}
